import { Component } from '@theme/component';
import { getStore, isColourOption } from '@theme/it-pdp-store';
import { StandardEvents } from '@shopify/events';

/**
 * IT PDP — add to bag.
 *
 * Owns three things: the CTA label, the hidden variant id Horizon's
 * product form submits, and the sold-out swap to Cartbite's Notify me slot.
 * Submission itself belongs to <product-form-component>, which this block
 * wraps rather than replaces.
 */
class ItPdpBuy extends Component {
  #unsubscribe;
  #state = null;

  /** Quantity per variant id currently in the cart. @type {Map<string, number>} */
  #cartQuantities = new Map();

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#store = getStore(this);

    // The cart decides whether a variant can still be added: once its line
    // sits at the stock ceiling the add is blocked here, with a notice, so
    // Shopify never has to refuse it and the drawer never opens on a failure.
    const { signal } = this.#abortController;
    this.#loadCart();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange, { signal });
    // The cart can change in another tab or on the way back through the
    // history; re-read it whenever this page comes back into view.
    window.addEventListener('pageshow', this.#onReturn, { signal });
    document.addEventListener('visibilitychange', this.#onReturn, { signal });

    this.#form = this.querySelector('form');
    this.#variantInput = this.querySelector('input[name="id"]');
    this.#label = this.querySelector('.it-btn__label');
    this.#cta = this.querySelector('.it-buy__cta');
    this.#button = this.querySelector('.it-btn--solid');
    this.#buyNow = this.querySelector('[data-it-buy-now]');

    // The guard lives on the CLICK, not the submit.
    //
    // Horizon binds `on:submit` through a single delegated listener on
    // `document` (see component.js), so by the time a submit event exists the
    // add is already on its way and stopping propagation at the form is not
    // reliable. Cancelling the click stops the submit event from ever being
    // dispatched, which is the only point where this is genuinely ours.
    this.#button?.addEventListener('click', this.#onActivate, { capture: true });
    this.#buyNow?.addEventListener('click', this.#onBuyNow);

    // Still guard the submit itself: the form can be submitted by pressing
    // Enter in a field, or by requestSubmit() from the sticky bar.
    this.#form?.addEventListener('submit', this.#onSubmit, { capture: true });

    this.#onExternalBuy = (event) => {
      if (event.detail?.blockId && event.detail.blockId !== this.dataset.blockId) return;
      // Nothing to add when the style or the chosen size is sold out: the
      // sticky bar's press opens Cartbite's Notify me instead.
      const variant = this.#state?.variant;
      if (this.#productSoldOut || (this.#state?.complete && (!variant || !variant.available))) {
        this.#askNotify();
        return;
      }
      // Route through the button so the sticky bar gets the same guard and
      // the same nudge as a press on the main CTA.
      this.#button?.click();
    };
    this.#onExternalBuyNow = () => this.#onBuyNow();
    this.#root = this.closest('[data-it-pdp]') ?? document;
    this.#root.addEventListener('it:request-add-to-cart', this.#onExternalBuy);
    this.#root.addEventListener('it:request-buy-now', this.#onExternalBuyNow);

    // GoKwik's Buy Now (snippets/gokwik-buy-now.liquid, in-flow and in the
    // sticky bar) is an inline onclick that posts whatever the hidden
    // variant input holds. Before a full selection that is the first variant,
    // not a choice the customer made, so it gets the same guard and the same
    // nudge as the native CTA. Capture on the root runs before the inline
    // handler, which is the only point where we can stop it.
    this.#root.addEventListener('click', this.#onGokwikClick, { capture: true });

    if (!this.#store) return;
    this.#unsubscribe = this.#store.subscribe(this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#unsubscribe?.();
    this.#button?.removeEventListener('click', this.#onActivate, { capture: true });
    this.#buyNow?.removeEventListener('click', this.#onBuyNow);
    this.#form?.removeEventListener('submit', this.#onSubmit, { capture: true });
    this.#root?.removeEventListener('it:request-add-to-cart', this.#onExternalBuy);
    this.#root?.removeEventListener('it:request-buy-now', this.#onExternalBuyNow);
    this.#root?.removeEventListener('click', this.#onGokwikClick, { capture: true });
  }

  #store = null;
  #form = null;
  #variantInput = null;
  #label = null;
  #cta = null;
  #button = null;
  #buyNow = null;
  #root = null;
  #onExternalBuy = null;
  #onExternalBuyNow = null;

  /**
   * Reads the cart through Horizon's cart-items component when it is on the
   * page (it dedupes in-flight fetches), else from the cart endpoint, then
   * re-renders so the notice and the guards reflect it.
   */
  async #loadCart() {
    let cart;
    try {
      /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
      const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
      cart = cartItems?.fetchCartData
        ? await cartItems.fetchCartData()
        : await fetch(`${Theme.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then((response) => response.json());
    } catch {
      return;
    }

    const quantities = new Map();
    for (const item of cart.items ?? []) {
      const key = String(item.variant_id);
      quantities.set(key, (quantities.get(key) ?? 0) + item.quantity);
    }
    this.#cartQuantities = quantities;

    if (this.#state) this.#render(this.#state);
  }

  #onReturn = () => {
    if (!document.hidden) this.#loadCart();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartChange = async (event) => {
    try {
      await event.promise;
    } catch {
      // A failed add still leaves the cart as it was; re-read it either way.
    }
    this.#loadCart();
  };

  /**
   * True when Shopify enforces a stock ceiling for the variant and the cart
   * already holds that many.
   * @param {any} variant
   */
  #atCartLimit(variant) {
    if (!variant?.available || !variant.inventoryManaged) return false;

    const max = Number(variant.inventoryQuantity) || 0;
    if (max <= 0) return false;

    return (this.#cartQuantities.get(String(variant.id)) ?? 0) >= max;
  }

  /** The whole style is gone, so Notify me is the only path. */
  get #productSoldOut() {
    return this.dataset.productSoldOut === 'true';
  }

  /**
   * Opens Cartbite's Notify me when its button has mounted; until then,
   * brings the slot (and its loader) into view.
   */
  #askNotify() {
    const button = this.refs.notifySlot?.querySelector('.gw-button-widget');
    if (button instanceof HTMLElement) {
      button.click();
      return;
    }
    this.refs.notifySlot?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** @type {number | undefined} */
  #waitTimeout;

  /**
   * While a sold-out variant is chosen and Cartbite has not yet mounted its
   * button, the slot shows a button-shaped loader. It gives up after a
   * while so a blocked or absent app does not leave a shimmer forever.
   * @param {boolean} soldOut
   */
  #syncNotifyLoader(soldOut) {
    const slot = this.refs.notifySlot;
    if (!(slot instanceof HTMLElement)) return;

    clearTimeout(this.#waitTimeout);
    if (!soldOut) {
      slot.removeAttribute('data-waiting');
      return;
    }

    slot.setAttribute('data-waiting', '');
    this.#waitTimeout = window.setTimeout(() => slot.removeAttribute('data-waiting'), 10000);
  }

  #flashNotice() {
    const { notice } = this.refs;
    if (!(notice instanceof HTMLElement)) return;

    notice.classList.remove('is-flash');
    void notice.offsetWidth;
    notice.classList.add('is-flash');
  }

  #render = (state) => {
    this.#state = state;

    const { variant, complete } = state;
    const soldOut = this.#productSoldOut || (complete && (!variant || !variant.available));
    const atLimit = complete && !soldOut && this.#atCartLimit(variant);

    if (this.refs.notice) this.refs.notice.hidden = !atLimit;

    if (variant && this.#variantInput) this.#variantInput.value = String(variant.id);

    if (this.#buyNow) this.#buyNow.disabled = !complete || soldOut || atLimit;

    if (this.#label) {
      // Ask for what is actually still missing. With a size picked and no
      // colour chosen the store's prompt is the colour option, and the button
      // says so — "Select Size" over an already-selected size read as broken.
      const askColour = !complete && isColourOption(state.prompt);
      this.#label.textContent = !complete
        ? askColour
          ? this.dataset.labelSelectColor || this.dataset.labelSelect
          : this.dataset.labelSelect
        : soldOut
        ? this.dataset.labelSoldout
        : this.dataset.labelAdd;
    }

    // Sold out hides the CTA entirely; Cartbite's Notify me button takes
    // its place in the slot below, so the customer still has a next step.
    if (this.#cta) this.#cta.hidden = soldOut;
    this.#syncNotifyLoader(soldOut);

    // GoKwik keys its own Buy Now on `button[name="add"].disabled`
    // (snippets/gokwik.liquid sync()) and re-reads it on every DOM mutation,
    // so the native button has to carry the sold-out state even while it is
    // hidden — otherwise GoKwik keeps a live Buy Now next to Notify me.
    //
    // Buy Now also waits for the choice to be made. GoKwik's sync() only ever
    // toggles `disabled`, never `hidden`, so this stays put across its
    // mutation passes. Before a size and colour are picked there is no variant
    // to buy, and a live Buy Now next to "Select Size" invites a click
    // that can only be refused. The in-flow copy sits inside .it-buy__cta and
    // the sticky bar's outside it; both are addressed here.
    if (this.#button) this.#button.disabled = soldOut;
    const hideBuyNow = soldOut || !complete;
    for (const button of this.#root.querySelectorAll('.gokwik-buy-now')) {
      button.hidden = hideBuyNow;
    }

    // Tell the rest of the page (sticky bar, price, gallery) what resolved.
    this.dispatchEvent(
      new CustomEvent('it:variant-change', {
        bubbles: true,
        detail: { variant, complete, soldOut, prompt: state.prompt },
      })
    );
  };

  /**
   * The option the store is asking for, or null when the choice is complete.
   * The same option the label names, so a press on "Select Color" nudges the
   * colour block and never the size grid.
   */
  #missingOption() {
    const state = this.#state;
    if (!state || !state.prompt) return null;

    return state.options.find((option) => option.name === state.prompt) ?? null;
  }

  /**
   * Point the shopper at the missing option: lift the button they pressed so
   * the press is seen to land, then hand the ask to the block that owns the
   * option, which scrolls into view and shakes.
   *
   * The lift is the answer to a press that otherwise did nothing visible:
   * on a scrolled page the size grid can be above the fold, and a button that
   * refuses silently reads as broken.
   *
   * @param {{ name: string }} option
   */
  #askFor(option) {
    this.#lift();

    this.#root.dispatchEvent(
      new CustomEvent('it:nudge-option', { bubbles: true, detail: { optionName: option.name } })
    );
  }

  #lift() {
    const buttons = [this.#button, this.#root.querySelector('.it-buybar__btn--solid')];

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      // Restart the animation: removing the class alone does not rewind it.
      button.classList.remove('is-nudge');
      void button.offsetWidth;
      button.classList.add('is-nudge');
    }
  }

  #blocked() {
    const state = this.#state;
    if (!state) return false;

    return (
      this.#productSoldOut ||
      Boolean(this.#missingOption()) ||
      !state.variant?.available ||
      this.#atCartLimit(state.variant)
    );
  }

  #onActivate = (event) => {
    if (!this.#blocked()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#productSoldOut) {
      this.#askNotify();
      return;
    }

    const missing = this.#missingOption();
    if (!missing) {
      this.#flashNotice();
      return;
    }

    this.#askFor(missing);
  };

  #onGokwikClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('.gokwik-buy-now') : null;
    if (!target || !this.#blocked()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#productSoldOut) {
      this.#askNotify();
      return;
    }

    const missing = this.#missingOption();
    if (!missing) {
      this.#flashNotice();
      return;
    }

    this.#askFor(missing);
  };

  #onSubmit = (event) => {
    if (!this.#blocked()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#productSoldOut) {
      this.#askNotify();
      return;
    }

    const missing = this.#missingOption();
    if (!missing) {
      this.#flashNotice();
      return;
    }

    this.#askFor(missing);
  };

  /**
   * Buy now: the same line, then straight to checkout.
   *
   * Deliberately not routed through <product-form-component> — that opens
   * the cart drawer on success, which is the opposite of what Buy now is
   * for. The add itself is the same /cart/add.js call the drawer makes.
   */
  #onBuyNow = async () => {
    if (this.#blocked()) {
      const missing = this.#productSoldOut ? null : this.#missingOption();
      if (this.#productSoldOut) {
        this.#askNotify();
      } else if (missing) {
        this.#askFor(missing);
      } else {
        this.#flashNotice();
      }
      return;
    }

    const variantId = this.#variantInput?.value;
    if (!variantId) return;

    if (this.#buyNow) this.#buyNow.disabled = true;

    try {
      // `Theme` is a lexical global from snippets/scripts.liquid, not a
      // window property, and cart_add_url already carries its `.js`.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(String(response.status));

      window.location.href = '/checkout';
    } catch {
      // Leave the customer on the page with a working Add to cart rather
      // than sending them to a checkout that has nothing in it.
      if (this.#buyNow) this.#buyNow.disabled = false;
    }
  };
}

if (!customElements.get('it-pdp-buy')) {
  customElements.define('it-pdp-buy', ItPdpBuy);
}
